import { h, createContext } from 'preact';
import { useState, useEffect, useContext } from 'preact/hooks';
import { DosageFormsPayload, RecommendedProducts } from './types';
import type { Product as ShopwareProduct } from './shopware/shopwareApi';

// read JSON Stuff from Twig.templates and make available as JSON in the context

type DataField<T> = {
  data: T | null;
  error: Error | null;
};

// Configuration for JSON data sources
const DATA_SOURCES = {
  dosageForms: {
    scriptId: 'product-dosageforms-json',
    logName: 'DosageForms',
  },
  recommendedProducts: {
    scriptId: 'recommended-products-json',
    logName: 'Recommended Products',
  },
  shopwareProductData: {
    scriptId: 'product-shopware-data-json',
    logName: 'Shopware Product Data',
  },
  baseProductPrices: {
    scriptId: 'base-product-prices-json',
    logName: 'Base Product Prices',
  },
  // Page-level price snapshot for the add-to-cart islands (ORT-3055), emitted once in layout.html.twig.
  // The islands' variants JSON (page-product-variants-json) is not read here: addToCartData.ts parses it itself.
  pageProductPrices: {
    scriptId: 'page-product-prices-json',
    logName: 'Page Product Prices',
  },
} as const;

type DataSourceKey = keyof typeof DATA_SOURCES;

type SubscriptionPlanInfo = {
  interval: string;
  amount: number;
  baseONumber: string;
};

export type SubscriptionPlansMap = Record<string, SubscriptionPlanInfo>;

type ShopwareProductDataMap = {
  products: Record<string, ShopwareProduct>;
  subscriptionPlans: SubscriptionPlansMap;
};

export type BaseProductPricesMap = Record<string, number>;

/** page-product-prices-json: snapshot prices per country (PageProductPriceCollector, PR #1509) */
export interface PageProductPriceEntry {
  oNumber: string;
  price: number;
  listPrice: number | null;
  isFrom: boolean;
  referencePrice: number | null;
  referenceUnit: string | null;
  fromAvailable: boolean;
  anyAvailable: boolean;
  coverageIncomplete: boolean;
  oNumbers: string[];
}
export interface PageONumberPriceEntry {
  productId: number;
  price: number;
  listPrice: number | null;
  referencePrice: number | null;
  referenceUnit: string | null;
  available: boolean;
}
export interface PageProductPrices {
  locale: string;
  currency: string;
  defaultCountry: string;
  countries: Record<
    string,
    { generatedAt: string; products: Record<string, PageProductPriceEntry>; onumbers: Record<string, PageONumberPriceEntry> }
  >;
}

type JsonProviderContextValue = {
  dosageForms: DataField<DosageFormsPayload>;
  recommendedProducts: DataField<RecommendedProducts>;
  shopwareProductData: DataField<ShopwareProductDataMap>;
  baseProductPrices: DataField<BaseProductPricesMap>;
  pageProductPrices: DataField<PageProductPrices>;
};

const createEmptyDataField = <T,>(): DataField<T> => ({ data: null, error: null });
const createInitialState = (): JsonProviderContextValue => ({
  dosageForms: createEmptyDataField<DosageFormsPayload>(),
  recommendedProducts: createEmptyDataField<RecommendedProducts>(),
  shopwareProductData: createEmptyDataField<ShopwareProductDataMap>(),
  baseProductPrices: createEmptyDataField<BaseProductPricesMap>(),
  pageProductPrices: createEmptyDataField<PageProductPrices>(),
});

const JsonProviderContext = createContext<JsonProviderContextValue | null>(null);

interface JsonProviderProps {
  children: preact.ComponentChildren;
}

export const JsonProvider = ({ children }: JsonProviderProps) => {
  const [state, setState] = useState<JsonProviderContextValue>(createInitialState());

  useEffect(() => {
    const updates: Partial<JsonProviderContextValue> = {};

    (Object.keys(DATA_SOURCES) as DataSourceKey[]).forEach((key) => {
      const source = DATA_SOURCES[key];
      try {
        const scriptTag = document.getElementById(source.scriptId);
        if (scriptTag) {
          const jsonContent = scriptTag.textContent || scriptTag.innerHTML;
          const parsed = JSON.parse(jsonContent);
          //console.log(`[eshop] [JsonProvider] ${source.logName} JSON`, jsonContent);
          console.log(`[eshop] [JsonProvider] ${source.logName} JSON parsed`, parsed);
          updates[key] = { data: parsed, error: null };
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(`Error parsing ${source.logName} JSON`);
        console.error(`[JsonProvider] ${source.logName} JSON parsing error:`, error);
        updates[key] = { data: null, error };
      }
    });

    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Note: the embedded shopwareProductData (cached HTML, Cloudflare TTL) is the first-paint
  // source only. Live, fresh data (stock, prices, promotions) is fetched per selected variant
  // via /pdp-data in ProductInfo, so the former Store-API batch refresh here was retired to
  // keep the PDP to a single Shopware call before first interaction.

  return <JsonProviderContext.Provider value={state}>{children}</JsonProviderContext.Provider>;
};

// Custom hook to use the JSON context
export const useJsonProvider = (): JsonProviderContextValue => {
  const context = useContext(JsonProviderContext);

  if (context === null) {
    throw new Error('useJsonProvider must be used within a JsonProvider');
  }

  return context;
};
